import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Clock, TrendingUp, BookOpen, ArrowRight, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import OperationsLayout, { PageHeader, KpiCard, SeverityBadge, StatusBadge } from './OperationsLayout';
import { listIncidents, getBaseline, getPromotionQueue } from '@/lib/operationsApi';

export default function OperationsDashboard() {
  const navigate = useNavigate();
  const [kpis, setKpis]         = useState(null);
  const [openIncs, setOpenIncs] = useState([]);
  const [queue, setQueue]       = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([
      getBaseline(),
      listIncidents({ status: "open", limit: 10 }),
      listIncidents({ status: "investigating", limit: 10 }),
      getPromotionQueue("pending_review"),
    ])
      .then(([baseline, open, investigating, promo]) => {
        setKpis(baseline);
        setOpenIncs([...(open.items || []), ...(investigating.items || [])]);
        setQueue(promo.items || []);
      })
      .catch((e) => toast.error("Failed to load dashboard: " + e.message))
      .finally(() => setLoading(false));
  }, []);

  const k = kpis?.kpis || {};

  return (
    <OperationsLayout>
      <PageHeader
        title="Operations Dashboard"
        subtitle="Real-time view of active incidents, knowledge growth, and pilot outcomes"
      />

      <div className="p-6 space-y-6">

        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard
            label="Open Incidents"
            value={loading ? "…" : k.open_incidents ?? 0}
            color={k.open_incidents > 3 ? "red" : "green"}
          />
          <KpiCard
            label="Avg MTTR"
            value={loading ? "…" : k.avg_mttr_minutes != null ? `${k.avg_mttr_minutes}m` : "—"}
            sub="mean time to resolve"
            color="blue"
          />
          <KpiCard
            label="Escalation Rate"
            value={loading ? "…" : k.escalation_pct != null ? `${k.escalation_pct}%` : "—"}
            color={k.escalation_pct > 30 ? "red" : "default"}
          />
          <KpiCard
            label="Knowledge Articles"
            value={loading ? "…" : k.verified_incident_count ?? 0}
            sub="verified incidents"
            color="green"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Active Incidents */}
          <div className="lg:col-span-2 bg-card border border-border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Active Incidents</span>
                {!loading && (
                  <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full">
                    {openIncs.length}
                  </span>
                )}
              </div>
              <button
                onClick={() => navigate("/operations/incidents")}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                View all <ArrowRight className="w-3 h-3" />
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : openIncs.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> No active incidents
              </div>
            ) : (
              <div className="divide-y divide-border">
                {openIncs.map((inc) => (
                  <button
                    key={inc.id}
                    onClick={() => navigate(`/operations/incidents/${inc.id}`)}
                    className="w-full text-left px-4 py-3 hover:bg-accent transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">{inc.title}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {[inc.vendor, inc.technology, inc.network_function].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <SeverityBadge severity={inc.severity} />
                        <StatusBadge status={inc.status} />
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Opened {formatAge(inc.created_at)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="space-y-4">
            {/* Promotion queue */}
            <div className="bg-card border border-border rounded-lg">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Needs SME Review</span>
                  {!loading && queue.length > 0 && (
                    <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 py-0.5 rounded-full font-medium">
                      {queue.length}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => navigate("/operations/knowledge")}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  Review <ArrowRight className="w-3 h-3" />
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              ) : queue.length === 0 ? (
                <div className="px-4 py-5 text-xs text-muted-foreground text-center">
                  Queue is clear
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {queue.slice(0, 5).map((p) => (
                    <button
                      key={p.id}
                      onClick={() => navigate("/operations/knowledge")}
                      className="w-full text-left px-4 py-2.5 hover:bg-accent transition-colors"
                    >
                      <div className="text-xs font-medium text-foreground truncate">
                        {p.vendor} {p.network_function}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        {p.root_cause?.slice(0, 60)}…
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Pilot status */}
            <div className="bg-card border border-border rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Pilot Targets</span>
              </div>
              <div className="space-y-2">
                <PilotTarget label="MTTR ≥ 25% reduction" met={false} note="Tracking" />
                <PilotTarget label="Escalation ≥ 20% reduction" met={false} note="Tracking" />
                <PilotTarget label="First-time fix ≥ 15% improvement" met={false} note="Tracking" />
              </div>
              <button
                onClick={() => navigate("/operations/executive")}
                className="mt-3 text-xs text-primary hover:underline flex items-center gap-1"
              >
                View full report <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>

        {/* Knowledge growth */}
        <div className="bg-card border border-border rounded-lg px-4 py-3">
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Knowledge Growth</span>
          </div>
          <div className="text-xs text-muted-foreground">
            {loading ? "Loading…" : (
              <>
                <span className="font-medium text-foreground">{k.verified_incident_count ?? 0}</span> verified incidents
                · <span className="font-medium text-foreground">{k.knowledge_reuse_count ?? 0}</span> knowledge reuse events
                · <span className="font-medium text-foreground">{queue.length}</span> pending SME review
              </>
            )}
          </div>
        </div>
      </div>
    </OperationsLayout>
  );
}

function PilotTarget({ label, met, note }) {
  return (
    <div className="flex items-center gap-2">
      {met
        ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
        : <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
      }
      <span className="text-xs text-foreground flex-1">{label}</span>
      <span className="text-xs text-muted-foreground">{note}</span>
    </div>
  );
}

function formatAge(isoStr) {
  if (!isoStr) return "";
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
